import { useMemo, useState } from 'react';
import { DyProductSourceDimensions } from 'src/constants';
import { useGetComponentTypes } from 'src/hooks/api/components/useGetComponentTypes';
import { ComponentTypes, DyProductSource } from 'src/interfaces';

import {
  ComponentModalState,
  RowItem,
} from '../modules/CustomerTriggeredEventsTable.interface';

export const useCustomerTriggeredEventsTable = ({
  rows,
  setRows,
}: {
  rows: RowItem[];
  setRows: React.Dispatch<React.SetStateAction<RowItem[]>>;
}) => {
  const { data: allComponentTypes } = useGetComponentTypes([
    `name.list=${ComponentTypes.COD}|${ComponentTypes.COD_THANK_YOU}|${ComponentTypes.SINGLE_SHELF}`,
  ]);
  const componentModalInitialState: ComponentModalState = {
    isOpen: false,
    rowId: null,
    replaceCompId: null,
  };

  const [menuAnchorEl, setMenuAnchorEl] = useState<{
    [key: string]: HTMLElement | null;
  }>({});
  const [componentModal, setComponentModal] = useState<ComponentModalState>(
    componentModalInitialState,
  );
  const [componentPosition, setComponentPosition] = useState(1);

  const componentTypeMap = useMemo(() => {
    const map = new Map<string, number>();
    allComponentTypes?.forEach((type) => {
      if (type.name) {
        const typeName = String(type.name);
        map.set(typeName, type.id);
        map.set(typeName.toLowerCase(), type.id);
      }
    });

    return map;
  }, [allComponentTypes]);

  const getFiltersForRow = (selectedRow: RowItem): string[] => {
    const selectedRowComponents = rows
      ?.find((row) => row.id === selectedRow.id)
      ?.components?.map((component) => component.id);

    const filters: string[] = [];

    if (selectedRowComponents && selectedRowComponents?.length > 0) {
      filters.push(`id.notlist=${selectedRowComponents.join('|')}`);
    }

    if (
      selectedRow.componentType === ComponentTypes.COD ||
      selectedRow.componentType === ComponentTypes.COD_THANK_YOU
    ) {
      const componentTypeId =
        componentTypeMap.get(selectedRow.componentType) ||
        componentTypeMap.get(selectedRow.componentType.toLowerCase());

      if (componentTypeId) {
        filters.push(`component_type_id=${componentTypeId}`);
      }
    } else if (selectedRow.componentType === ComponentTypes.SINGLE_SHELF) {
      const singleShelfTypeId =
        componentTypeMap.get(ComponentTypes.SINGLE_SHELF) ||
        componentTypeMap.get(ComponentTypes.SINGLE_SHELF.toLowerCase());

      if (singleShelfTypeId) {
        filters.push(`component_type_id=${singleShelfTypeId}`);
      }

      if (selectedRow.componentVariant === DyProductSource.DYR) {
        filters.push(
          `products_source=${DyProductSource.DYR}`,
          `height=${DyProductSourceDimensions.DYR.height}`,
          `width=${DyProductSourceDimensions.DYR.width}`,
        );
      } else if (selectedRow.componentVariant === DyProductSource.DYS) {
        filters.push(
          `products_source=${DyProductSource.DYS}`,
          `height=${DyProductSourceDimensions.DYS.height}`,
          `width=${DyProductSourceDimensions.DYS.width}`,
        );
      }
    }

    return filters;
  };

  const handleMenuClose = (key: string) => {
    setMenuAnchorEl((previousMenuAnchorEl) => ({
      ...previousMenuAnchorEl,
      [key]: null,
    }));
  };

  const openComponentModal = (
    rowId: string,
    replaceCompId: string | null = null,
  ) => {
    setComponentModal({
      isOpen: true,
      rowId,
      replaceCompId,
    });
  };

  const handleCloseComponentModal = () => {
    setComponentModal(componentModalInitialState);
  };

  const handleSwapComponent = (rowId: string, compId: string) => {
    openComponentModal(rowId, compId);
  };

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    rowId: string,
    compId?: string,
  ) => {
    event.stopPropagation();
    const key = compId ? `${rowId}_${compId}` : rowId;
    setMenuAnchorEl((previousMenuAnchorEl) => ({
      ...previousMenuAnchorEl,
      [key]: event.currentTarget,
    }));
  };

  const handleDeleteComponent = (rowId: string, compId: string) => {
    handleMenuClose(`${rowId}_${compId}`);
    setRows((previousRows) =>
      previousRows.map((rowItem) => {
        if (rowItem.id !== rowId) return rowItem;

        return {
          ...rowItem,
          components: (rowItem.components ?? []).filter(
            (componentItem) => componentItem.id !== compId,
          ),
        };
      }),
    );
  };

  return {
    getFiltersForRow,
    handleMenuOpen,
    handleDeleteComponent,
    handleMenuClose,
    menuAnchorEl,
    componentModal,
    handleCloseComponentModal,
    openComponentModal,
    handleSwapComponent,
    componentPosition,
    setComponentPosition,
  };
};
